import { useState, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { GraduationCap, Building2, Award, Clock, MapPin, Languages } from "lucide-react";
export default function TrustBadges() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.2,
    once: false
  });
  const [counterInView, setCounterInView] = useState(false);
  const [yearsCount, setYearsCount] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => {
      setCounterInView(entry.isIntersecting);
    }, {
      threshold: 0.2
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref]);
  useEffect(() => {
    if (!counterInView) {
      setYearsCount(0);
      return;
    }
    let animationFrameId: number;
    const startTime = performance.now();
    const duration = 1200;
    const target = 25;
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - (1 - progress) * (1 - progress);
      const currentVal = Math.floor(easeProgress * target);
      setYearsCount(currentVal);
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setYearsCount(target);
      }
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [counterInView]);
  const credentials = [{
    icon: GraduationCap,
    label: "Mount Sinai Medical Center",
    subLabel: "Cardiology Fellowship"
  }, {
    icon: Building2,
    label: "Mass General / Harvard",
    subLabel: "Interventional Cardiology Fellowship"
  }, {
    icon: Award,
    label: "FACC",
    subLabel: "ACC Fellow"
  }, {
    icon: Clock,
    label: `${yearsCount}+ Years`,
    subLabel: "Medical Experience"
  }, {
    icon: MapPin,
    label: "Two Locations",
    subLabel: "South Florida"
  }, {
    icon: Languages,
    label: "Three Languages",
    subLabel: "English, Spanish & Portuguese"
  }];
  return <section ref={ref} aria-label="Credentials and Accreditations" className={`bg-[#f7f8fa] border-b-2 border-primary py-5 transition-all duration-400 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-center justify-center sm:justify-between gap-y-6 gap-x-10 md:gap-8">
          {credentials.map((item, index) => {
          const Icon = item.icon;
          return <div key={index} data-index={index} className="flex items-center gap-2.5 text-[#152238]">
                <Icon className="w-5 h-5 text-[#152238] stroke-[1.75] shrink-0" />
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-[#152238] leading-tight whitespace-nowrap">
                    {item.label}
                  </span>
                  <span className="text-xs text-gray-500 leading-tight whitespace-nowrap">
                    {item.subLabel}
                  </span>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
